import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import { gpsDevicesService } from "../services/gps-devices.service";
import type { CreateGpsDeviceRequest, GpsDeviceListParams, UpdateGpsDeviceRequest } from "../types";

export const gpsDevicesKeys = {
	all: ["gps-devices"] as const,
	lists: () => [...gpsDevicesKeys.all, "list"] as const,
	list: (params: GpsDeviceListParams) => [...gpsDevicesKeys.lists(), params] as const,
	details: () => [...gpsDevicesKeys.all, "detail"] as const,
	detail: (id: string) => [...gpsDevicesKeys.details(), id] as const,
};

// ─── useGpsDevices (list) ───────────────────────────────────────────────
export function useGpsDevices(params: GpsDeviceListParams) {
	return useQuery({
		queryKey: gpsDevicesKeys.list(params),
		queryFn: () => gpsDevicesService.list(params),
		enabled: !!params.organizationId,
	});
}

// ─── useGpsDevice (getById) ─────────────────────────────────────────────
export function useGpsDevice(id: string) {
	return useQuery({
		queryKey: gpsDevicesKeys.detail(id),
		queryFn: () => gpsDevicesService.getById(id),
		enabled: !!id,
	});
}

// ─── useCreateGpsDevice ─────────────────────────────────────────────────
export function useCreateGpsDevice() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: CreateGpsDeviceRequest) => gpsDevicesService.create(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: gpsDevicesKeys.lists() });
			message.success("GPS qurilma muvaffaqiyatli yaratildi");
		},
	});
}

// ─── useUpdateGpsDevice ─────────────────────────────────────────────────
export function useUpdateGpsDevice() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: UpdateGpsDeviceRequest }) =>
			gpsDevicesService.update(id, data),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: gpsDevicesKeys.lists() });
			queryClient.invalidateQueries({ queryKey: gpsDevicesKeys.detail(variables.id) });
			message.success("GPS qurilma muvaffaqiyatli yangilandi");
		},
	});
}

// ─── useDeleteGpsDevice ─────────────────────────────────────────────────
export function useDeleteGpsDevice() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => gpsDevicesService.delete(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: gpsDevicesKeys.lists() });
			message.success("GPS qurilma muvaffaqiyatli o'chirildi");
		},
	});
}
